if (!customElements.get('size-dimension-controller')) {
  class SizeDimensionController extends HTMLElement {
    constructor() {
      super();
      this.dropdowns = this.querySelectorAll('div[is="select-dropdown"]');
      this.dropdowns.forEach((dropdown) => {
        const select = dropdown.nextElementSibling;
        if (select && select.tagName === 'SELECT') {
          select.addEventListener('change', this.handleOptionSelection.bind(this));
        }
      });

      // Format dimensions on initialization
      this.formatDimensions();

      // Check for initial variant match
      this.checkInitialVariantMatch();

      // Remove the disabled attribute from the size drawer button
      const sizeDrawer = this.closest('size-drawer');
      if (sizeDrawer) {
        const sizeDrawerButton = sizeDrawer.previousElementSibling;
        if (sizeDrawerButton && sizeDrawerButton.classList.contains('size-drawer__button')) {
          sizeDrawerButton.removeAttribute('disabled');
        }
      }
    }

    formatDimensions() {
      // Find all dimension containers
      const dimensionContainers = this.querySelectorAll('.product-size-dimension-content__dimensions');

      dimensionContainers.forEach((container) => {
        // Find the metafield content inside the container
        const metafieldContent = container.querySelector('.metafield-rich_text_field');
        if (!metafieldContent) return;

        // Only process if there are paragraphs
        const allParagraphs = metafieldContent.querySelectorAll('p');
        if (allParagraphs.length < 2) return;

        // First check if any paragraph contains just "---"
        let hasDividers = false;
        for (const p of allParagraphs) {
          if (p.textContent.trim() === '---') {
            hasDividers = true;
            break;
          }
        }

        // Only format if dividers are found
        if (!hasDividers) return;

        // Instead of replacing the entire content, let's just style the relevant paragraphs
        for (let i = 0; i < allParagraphs.length; i++) {
          const paragraph = allParagraphs[i];
          const nextParagraph = i + 1 < allParagraphs.length ? allParagraphs[i + 1] : null;

          // If this paragraph has strong tag and next one is a divider
          if (paragraph.querySelector('strong') && nextParagraph && nextParagraph.textContent.trim() === '---') {
            // Style this paragraph as a row
            paragraph.style.display = 'flex';
            paragraph.style.justifyContent = 'space-between';
            paragraph.style.padding = '1.5rem 0';
            paragraph.style.margin = '0';
            paragraph.style.borderBottom = '1px solid var(--border-default, #dededb)';

            // Make the strong tag align left and rest align right
            const strongElement = paragraph.querySelector('strong');
            if (strongElement) {
              strongElement.style.fontWeight = '600';

              // Remove the colon from the strong tag text if it exists
              if (strongElement.textContent.includes(':')) {
                strongElement.textContent = strongElement.textContent.replace(':', '');
              }
            }

            // Hide the divider paragraph
            nextParagraph.style.display = 'none';

            // Skip the divider in the next iteration
            i++;
          }
        }
      });
    }

    handleOptionSelection(e) {
      const selectedValues = [];

      this.dropdowns.forEach((element) => {
        // First remove the selected class from all option items
        element.querySelectorAll('[role="option"]').forEach((option) => {
          option.classList.remove('select-dropdown__item--selected');
          option.setAttribute('aria-selected', 'false');
        });

        // Use option:checked instead of option[selected] to correctly get the current selection
        const select = element.nextElementSibling;
        if (!select || select.tagName !== 'SELECT') return;

        const checkedOption = select.querySelector('option:checked');

        if (checkedOption?.dataset.optionHandle) {
          const optionHandle = checkedOption.dataset.optionHandle;

          selectedValues.push(optionHandle);

          // Add selected class to the matching list item
          const matchingOption = element.querySelector(`[role="option"][data-value-handle="${optionHandle}"]`);
          if (matchingOption) {
            matchingOption.classList.add('select-dropdown__item--selected');
            matchingOption.setAttribute('aria-selected', 'true');
          }
        }
      });

      // Update available options for other dropdowns
      this.updateAvailableOptions();

      // Check if configuration changed and auto-select first available size
      const triggerSelect = e.target.closest('select');
      if (triggerSelect) {
        const selectName = triggerSelect.name;
        const optionName = selectName.replace('select-dimension--', '').replace(/\s+/g, '').toLowerCase();
        const isConfigurationOption = !optionName.includes('size') && !optionName.includes('seater');

        if (isConfigurationOption) {
          this.autoSelectFirstAvailableSize();
        }
      }

      // Select all elements with the class 'product-size-dimension-contents__item'
      const dimensionContents = this.querySelectorAll('.product-size-dimension-contents__item');

      // Get updated selected values after auto-selection
      const finalSelectedValues = [];
      this.dropdowns.forEach((element) => {
        const select = element.nextElementSibling;
        if (!select || select.tagName !== 'SELECT') return;

        const checkedOption = select.querySelector('option:checked');
        if (checkedOption?.dataset.optionHandle) {
          finalSelectedValues.push(checkedOption.dataset.optionHandle);
        }
      });

      // Don't do anything if no values are selected
      if (finalSelectedValues.length === 0) {
        return;
      }

      // Find matching content based on data attributes
      const matchingContents = [];
      dimensionContents.forEach((content) => {
        // Check if this content matches all selected values by comparing data attributes
        const isMatch = finalSelectedValues.every((selectedValue) => {
          // Check all data-option-* attributes on the content element
          for (const [key, value] of Object.entries(content.dataset)) {
            if (key.startsWith('option') && value === selectedValue) {
              return true;
            }
          }
          return false;
        });

        if (isMatch) {
          matchingContents.push(content);
        }
      });

      // Show first matching content, hide others
      if (matchingContents.length > 0) {
        dimensionContents.forEach((content, index) => {
          if (content === matchingContents[0]) {
            content.classList.remove('hidden');
          } else {
            content.classList.add('hidden');
          }
        });
      } else {
        // If no matches found, hide all content
        dimensionContents.forEach((content) => {
          content.classList.add('hidden');
        });
      }
    }

    autoSelectFirstAvailableSize() {
      // Find the size dropdown
      const sizeDropdown = Array.from(this.dropdowns).find((dropdown) => {
        const select = dropdown.nextElementSibling;
        if (!select || select.tagName !== 'SELECT') return false;
        const selectName = select.name;
        const optionName = selectName.replace('select-dimension--', '').replace(/\s+/g, '').toLowerCase();
        return optionName.includes('size') || optionName.includes('seater');
      });

      if (!sizeDropdown) return;

      // Find the first enabled option item
      const enabledOption = sizeDropdown.querySelector('[role="option"]:not(.select-dropdown__item--disabled)');

      if (enabledOption) {
        const optionHandle = enabledOption.dataset.valueHandle;
        const select = sizeDropdown.nextElementSibling;

        // Update the select element
        const matchingOption = select.querySelector(`option[data-option-handle="${optionHandle}"]`);
        if (matchingOption) {
          // Clear all selections first
          select.querySelectorAll('option').forEach((opt) => (opt.selected = false));
          // Select the new option
          matchingOption.selected = true;

          // Update the dropdown label
          const labelElement = sizeDropdown.querySelector('.select-dropdown__label');
          if (labelElement) {
            labelElement.textContent = matchingOption.textContent.trim();
          }

          // Update option items appearance
          sizeDropdown.querySelectorAll('[role="option"]').forEach((option) => {
            option.classList.remove('select-dropdown__item--selected');
            option.setAttribute('aria-selected', 'false');
          });
          enabledOption.classList.add('select-dropdown__item--selected');
          enabledOption.setAttribute('aria-selected', 'true');
        }
      }
    }

    updateAvailableOptions() {
      // Get currently selected values from all dropdowns
      const currentSelections = {};

      this.dropdowns.forEach((dropdown) => {
        const select = dropdown.nextElementSibling;
        if (!select || select.tagName !== 'SELECT') return;

        const checkedOption = select.querySelector('option:checked');
        if (checkedOption?.dataset.optionHandle) {
          // Get the option name from the select element's name attribute
          const selectName = select.name;
          const optionName = selectName.replace('select-dimension--', '').replace(/\s+/g, '').toLowerCase();
          currentSelections[optionName] = checkedOption.dataset.optionHandle;
        }
      });

      // Get all available variant combinations
      const dimensionContents = this.querySelectorAll('.product-size-dimension-contents__item');
      const availableCombinations = [];

      dimensionContents.forEach((content) => {
        const combination = {};
        for (const [key, value] of Object.entries(content.dataset)) {
          if (key.startsWith('option')) {
            const optionName = key.replace('option', '').toLowerCase();
            combination[optionName] = value;
          }
        }
        if (Object.keys(combination).length > 0) {
          availableCombinations.push(combination);
        }
      });

      // Update each dropdown to enable/disable options
      this.dropdowns.forEach((dropdown) => {
        const select = dropdown.nextElementSibling;
        if (!select || select.tagName !== 'SELECT') return;

        const selectName = select.name;
        const currentOptionName = selectName.replace('select-dimension--', '').replace(/\s+/g, '').toLowerCase();

        // Only apply disabling logic to size options, not configuration
        const isSizeOption = currentOptionName.includes('size') || currentOptionName.includes('seater');

        if (!isSizeOption) {
          // For non-size options (like configuration), keep all option items enabled
          const options = dropdown.querySelectorAll('[role="option"]');
          options.forEach((option) => {
            option.classList.remove('select-dropdown__item--disabled');
          });
          return;
        }

        // Get all option items in this dropdown
        const options = dropdown.querySelectorAll('[role="option"]');

        options.forEach((option) => {
          const optionHandle = option.dataset.valueHandle;

          // Check if this option is available given current selections
          const isAvailable = availableCombinations.some((combination) => {
            // Check if this combination matches current selections for other options
            // and includes this option value
            const matchesOtherSelections = Object.entries(currentSelections).every(
              ([selectedOptionName, selectedValue]) => {
                if (selectedOptionName === currentOptionName) {
                  // For the current dropdown, check if this specific option value exists
                  return combination[selectedOptionName] === optionHandle;
                } else {
                  // For other dropdowns, check if the selected value matches
                  return combination[selectedOptionName] === selectedValue;
                }
              }
            );

            return matchesOtherSelections && combination[currentOptionName] === optionHandle;
          });

          // Enable/disable the option item
          if (isAvailable) {
            option.classList.remove('select-dropdown__item--disabled');
          } else {
            option.classList.add('select-dropdown__item--disabled');
          }
        });
      });
    }

    checkInitialVariantMatch() {
      // Get the selected options from the controller element data attributes
      const selectedOptions = {};

      // Extract all data-option-* attributes from the controller
      for (const [key, value] of Object.entries(this.dataset)) {
        if (key.startsWith('option')) {
          // Convert camelCase back to kebab-case for the option name
          const optionName = key.replace('option', '').toLowerCase();
          selectedOptions[optionName] = value;
        }
      }

      // If no selected options, do nothing
      if (Object.keys(selectedOptions).length === 0) {
        return;
      }

      // Find all dimension content items
      const dimensionContents = this.querySelectorAll('.product-size-dimension-contents__item');

      // Find exact match based on data attributes
      let matchingContent = null;

      dimensionContents.forEach((content) => {
        // Check if this content has matching data attributes for all selected options
        const isMatch = Object.entries(selectedOptions).every(([optionName, optionValue]) => {
          const dataKey = `option${optionName.charAt(0).toUpperCase()}${optionName.slice(1)}`;
          return content.dataset[dataKey] === optionValue;
        });

        if (isMatch && !matchingContent) {
          matchingContent = content;
        }
      });

      if (matchingContent) {
        // Hide all dimension contents
        dimensionContents.forEach((content) => {
          content.classList.add('hidden');
        });

        // Show the matching content
        matchingContent.classList.remove('hidden');

        // Update dropdown selections based on the matched content's data attributes
        this.updateDropdownsForContent(matchingContent);
      }

      // Update available options after initial setup
      this.updateAvailableOptions();
      // If no match found, do nothing (keep current state)
    }

    updateDropdownsForContent(content) {
      // Get all data-option-* attributes from the content element
      const contentOptions = {};
      for (const [key, value] of Object.entries(content.dataset)) {
        if (key.startsWith('option')) {
          const optionName = key.replace('option', '').toLowerCase();
          contentOptions[optionName] = value;
        }
      }

      // Update each dropdown based on the content's data attributes
      this.dropdowns.forEach((dropdown) => {
        const select = dropdown.nextElementSibling;
        if (!select || select.tagName !== 'SELECT') return;

        const options = select.querySelectorAll('option');

        // Find the option that matches the content's data attribute
        options.forEach((option) => {
          const optionHandle = option.dataset.optionHandle;

          // Check if this option handle matches any of the content's option values
          const shouldSelect = Object.values(contentOptions).includes(optionHandle);

          if (shouldSelect) {
            // Set this option as selected
            option.selected = true;

            // Update the dropdown label text
            const labelElement = dropdown.querySelector('.select-dropdown__label');
            if (labelElement) {
              labelElement.textContent = option.textContent.trim();
            }

            // Update the dropdown option item appearance
            const matchingOption = dropdown.querySelector(`[role="option"][data-value-handle="${optionHandle}"]`);
            if (matchingOption) {
              // Remove selected class from all option items in this dropdown
              dropdown.querySelectorAll('[role="option"]').forEach((opt) => {
                opt.classList.remove('select-dropdown__item--selected');
                opt.setAttribute('aria-selected', 'false');
              });
              // Add selected class to the matching option item
              matchingOption.classList.add('select-dropdown__item--selected');
              matchingOption.setAttribute('aria-selected', 'true');
            }
          }
        });
      });
    }
  }

  customElements.define('size-dimension-controller', SizeDimensionController);
}
